import { ChevronDown, ChevronRight, Loader2, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import { cn } from '../../../../../lib/utils'
import { ChatToolCall } from '../../../../../models/agentConversation'
import { DEFAULT_TOOL_ICON, TOOL_ICONS } from './toolIcons'

interface Props {
  call: ChatToolCall
  /** Still in flight — spins in place of the chevron until the result lands. */
  running?: boolean
}

/**
 * One line in scrollback per tool call — no card, no fill, so a run of them
 * reads as a quiet log next to the assistant's prose rather than a stack of
 * boxes. Collapsed by default for successful reads (icon + label + result
 * summary); expanded by default for errors so the failure is visible without
 * a click. Rows with no args don't expand at all. A running call renders the
 * same row, so it converts in place when the result lands.
 */
export function ToolCallCard({ call, running = false }: Props) {
  // Presence of the field marks the failure, not its content — a tool that
  // raised with an empty message is still a failed call. `!= null` also covers
  // a record persisted without the key at all.
  const hasError = call.error != null
  // Without args the panel is just the tool name, i.e. the label again — nothing to reveal.
  const expandable = Boolean(call.argsSummary)
  const [expanded, setExpanded] = useState(hasError && expandable)
  // The tool's own glyph carries what was touched; a failure overrides it,
  // since "this one broke" outranks "this one was about documents".
  const Icon = hasError ? TriangleAlert : (TOOL_ICONS[call.icon] ?? DEFAULT_TOOL_ICON)
  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <div className="flex flex-col text-[13px]">
      <button
        type="button"
        disabled={!expandable}
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'text-muted-foreground flex w-full items-center gap-1.5 text-left',
          expandable && 'hover:text-foreground transition-colors'
        )}
      >
        {/* Spinner while in flight; empty when there is nothing to expand, so a run of rows stays aligned. */}
        {running ? (
          <Loader2 className="h-3 w-3 shrink-0 animate-spin opacity-60" />
        ) : expandable ? (
          <Chevron className="h-3 w-3 shrink-0 opacity-60" />
        ) : (
          <span className="h-3 w-3 shrink-0" />
        )}
        <Icon className={cn('h-3.5 w-3.5 shrink-0', hasError && 'text-red-500')} />
        <span className={cn('text-foreground font-medium', hasError && 'text-red-500')}>{call.label}</span>
        {!hasError && call.resultSummary && <span>— {call.resultSummary}</span>}
        {hasError && <span className="text-red-500">— {call.error}</span>}
      </button>
      {expanded && (
        <div className="text-muted-foreground mt-1 ml-6 space-y-0.5 text-xs">
          <div>
            <span className="font-medium">Args:</span> <code>{call.argsSummary}</code>
          </div>
          <div>
            <span className="font-medium">Tool:</span> <code>{call.tool}</code>
          </div>
        </div>
      )}
    </div>
  )
}
